// ============================================================================
// Contenu de l'offre « Qualifications professionnelles »
// Design validé le 16/09/2026 :
//   docs/superpowers/specs/2026-09-16-qualifications-professionnelles-design.md
//
// ⚠️ LIGNES ROUGES — ne pas les enfreindre en éditant ce fichier :
//   1. AUCUN chiffre de performance (taux d'acceptation, nombre de dossiers,
//      note client) et AUCUN client nommé. Le site s'est déjà fait piéger deux
//      fois là-dessus — risque L. 121-2 du Code de la consommation.
//   2. Hupsoo NE DÉLIVRE RIEN. La décision appartient à Qualibat, Qualit'EnR,
//      Qualifelec ou à l'organisme certificateur accrédité. Interdit :
//      « nous vous obtenons », « qualification garantie », « clé en main ».
//   3. Aucune garantie d'obtention, sous aucune formulation.
//   4. AUCUN LOGO d'organisme (Qualibat, RGE, QualiPV, Qualifelec, ISO, MASE) :
//      ce sont des marques réservées aux entreprises qualifiées. Texte seulement.
//   5. La RS7481 porte sur les MARCHÉS PUBLICS : elle ne couvre pas cette offre.
//   6. AUCUN montant de frais d'organisme ici : ils changent chaque année.
//   7. RGE : fait énoncé (la mention conditionne l'accès des clients finaux à
//      MaPrimeRénov' et aux CEE). Hupsoo n'intervient jamais dans ces aides.
//   8. Chaque fait des cartes FAMILLES a été vérifié sur le site officiel de
//      l'organisme — voir le champ `sources`. Ne rien ajouter sans vérifier.
//
// Les clés `icon` sont résolues par ICONES_QUALIFICATIONS dans App.js.
// ============================================================================

export const ACCROCHE =
  "Le dossier n'est pas refusé parce qu'il est mauvais. Il n'est jamais déposé.";

// --- Bloc 1 : le constat ---------------------------------------------------
export const CONSTAT = {
  titre: "Une qualification se demande. Elle ne s'obtient pas en travaillant bien.",
  texte:
    "Dans un marché public de travaux, l'acheteur demande fréquemment un certificat de " +
    "qualification — ou les preuves équivalentes que vous êtes libre de produire à la place. " +
    "Sans l'un ni les autres, votre offre part avec un handicap qu'aucun prix ne rattrape. " +
    "Côté marché privé, la mention RGE conditionne l'accès de vos clients particuliers à " +
    "MaPrimeRénov' et, pour l'essentiel des travaux de rénovation énergétique, aux certificats " +
    "d'économies d'énergie : sans elle, ce n'est pas vous " +
    "qu'ils appellent.",
};

// --- Bloc 2 : les quatre familles ------------------------------------------
export const FAMILLES = [
  {
    icon: "building",
    nom: "Qualibat",
    pourQui: "Entreprises du bâtiment, tous corps d'état.",
    ceQuOuvre:
      "La qualification atteste de vos moyens et de vos références sur une activité précise. " +
      "La mention RGE s'y ajoute pour les travaux d'efficacité énergétique.",
    sources: ["https://www.qualibat.com/nos-qualifications-certifications"],
  },
  // ⚠️ Qualit'EnR : pages inaccessibles aux accès automatisés (403). À relire sur un navigateur réel avant publication.
  {
    icon: "sun",
    nom: "Qualit'EnR — QualiPV, QualiPAC, QualiBois, QualiSol",
    pourQui: "Installateurs d'énergies renouvelables : photovoltaïque, pompes à chaleur, bois, solaire thermique.",
    ceQuOuvre:
      "Une marque par énergie. Selon l'activité couverte, la qualification peut porter la mention RGE.",
    sources: [
      "https://www.qualit-enr.org/qualifications/qualipac/ (accès automatisé refusé — 403 — le 17/09/2026 : page NON lue directement)",
      "https://www.qualit-enr.org/accueil-pro/ (accès automatisé refusé — 403 — le 17/09/2026 : page NON lue directement)",
    ],
  },
  {
    icon: "plug",
    nom: "Qualifelec",
    pourQui: "Entreprises d'installation électrique, y compris bornes de recharge.",
    ceQuOuvre:
      "Qualification par domaine d'activité, avec mention RGE possible selon l'activité.",
    sources: ["https://pros.qualifelec.fr/"],
  },
  {
    icon: "clipboard",
    nom: "ISO 9001, ISO 14001, MASE",
    pourQui: "Entreprises à qui un donneur d'ordre réclame un système de management formalisé.",
    ceQuOuvre:
      "Ce ne sont pas des qualifications de métier mais des certifications de système : " +
      "elles portent sur votre organisation, votre traçabilité et vos pratiques de sécurité. " +
      "La certification est délivrée par un organisme accrédité, à l'issue d'un audit.",
    // `sources` vide, honnêtement : ce que dit cette carte (système de management, audit,
    // délivrance par un organisme accrédité) est un fait générique sur ISO 9001 / ISO 14001 /
    // MASE, non rattaché à une page officielle précise vérifiée le 17/09/2026 comme pour les
    // trois familles ci-dessus. Ne pas ajouter de lien ici sans l'avoir réellement consulté.
    sources: [],
  },
];

// --- Bloc 3 : le vrai frein ------------------------------------------------
export const FREIN = {
  titre: "Ce qui bloque, ce n'est pas le formulaire. Ce sont les pièces.",
  texte:
    "La partie administrative se remplit vite : l'organisme récupère l'essentiel de vos données " +
    "légales à partir de votre SIRET. Ce qui fait échouer les dossiers, c'est tout le reste — " +
    "les preuves que personne n'a sous la main le jour où il faut les fournir.",
  pieces: [
    "Attestations de bonne exécution signées par vos clients",
    "Photos de chantier exploitables, rattachées à la bonne référence",
    "Moyens humains : effectifs, qualifications et titres de vos compagnons",
    "Moyens matériels : ce que vous possédez, ce que vous louez",
    "Assurances à jour, décennale comprise",
    "Éléments comptables de l'exercice demandé",
  ],
};

// --- Bloc 4 : la méthode ---------------------------------------------------
export const ETAPES = [
  {
    n: "01",
    icon: "search",
    titre: "Diagnostic — gratuit",
    texte:
      "Quelle qualification correspond réellement à votre activité, dans quelle catégorie, et " +
      "êtes-vous éligible aujourd'hui — références, ancienneté, effectif. Si la réponse est non, " +
      "nous vous disons ce qu'il manque et quand revenir, plutôt que de vous vendre un dossier " +
      "qui sera refusé.",
  },
  {
    n: "02",
    icon: "inbox",
    titre: "Collecte des pièces",
    texte:
      "Vous recevez une liste nominative, pièce par pièce, avec le modèle d'attestation de bonne " +
      "exécution à faire signer. Nous relançons jusqu'à ce que le dossier soit complet : c'est " +
      "l'étape où beaucoup de demandes s'arrêtent.",
  },
  {
    n: "03",
    icon: "file",
    titre: "Montage et dépôt",
    texte:
      "Rédaction et saisie du dossier sur le portail de l'organisme, sur le compte de votre " +
      "entreprise — jamais sur un compte cabinet. Vous gardez la main sur vos accès et sur vos " +
      "données.",
  },
  {
    n: "04",
    icon: "repeat",
    titre: "Suivi jusqu'à la décision",
    texte:
      "L'organisme revient souvent avec des demandes de compléments. Nous y répondons " +
      "pour vous, dans les délais impartis, jusqu'à la décision de la commission.",
  },
];

// --- Bloc 5 : les réserves -------------------------------------------------
export const RESERVES = [
  "Aucune garantie d'obtention : la décision appartient à l'organisme, jamais au conseil. Nous montons le dossier, la commission décide.",
  "Nous ne délivrons aucune qualification et ne sommes ni organisme de qualification, ni organisme certificateur, ni auditeur.",
  "Nous n'inventons aucune référence et ne rédigeons aucune attestation à la place de votre client : un dossier constitué de pièces complaisantes vous expose bien plus qu'il ne vous sert.",
  "Frais d'instruction de l'organisme en sus, communiqués au diagnostic. Pour les certifications de système, l'audit est facturé séparément par l'organisme accrédité.",
  "La certification RS7481 de notre consultante porte sur les marchés publics. Elle ne couvre pas les qualifications professionnelles, qui relèvent des règles propres à chaque organisme.",
  "La mention RGE conditionne l'accès de vos clients à MaPrimeRénov' et aux certificats d'économies d'énergie. Nous n'intervenons pas dans ces dispositifs, à aucun titre.",
];

// --- Bloc 6 : les engagements ----------------------------------------------
export const ENGAGEMENTS = [
  "Diagnostic gratuit, et réponse franche si vous n'êtes pas éligible aujourd'hui.",
  "Un prix annoncé avant de commencer, pas un compteur qui tourne.",
  "Les demandes de compléments de l'organisme sont comprises dans le forfait.",
  "Le dossier est déposé au nom de votre entreprise, sur son propre compte.",
  "Une seule interlocutrice, partout en France, à distance.",
];

// --- Bloc 7 : la carte d'offre ---------------------------------------------
export const OFFRE = {
  titre: "Montage de votre dossier de qualification",
  description:
    "De l'identification de la bonne qualification jusqu'à la décision de la commission, " +
    "demandes de compléments comprises.",
  features: [
    "Diagnostic d'éligibilité gratuit",
    "Liste de pièces personnalisée et modèles d'attestation",
    "Relances jusqu'à complétude du dossier",
    "Saisie et dépôt sur le compte de votre entreprise",
    "Réponses aux demandes de compléments jusqu'à la décision",
    "Mention RGE traitée avec la qualification quand elle s'applique",
  ],
  cta: "Vérifier gratuitement mon éligibilité",
};

// --- Bloc 8 : FAQ ----------------------------------------------------------
export const FAQ = [
  {
    question: "Combien de temps faut-il pour obtenir une qualification ?",
    answer:
      "Le délai dépend de l'organisme, de la complétude du dossier et du calendrier de sa " +
      "commission. Nous vous annonçons le délai applicable à votre cas au diagnostic, une fois " +
      "la qualification visée identifiée. Le facteur sur lequel vous pesez vraiment, c'est la " +
      "vitesse à laquelle vos pièces nous parviennent.",
  },
  {
    question: "Le dossier est-il déposé à mon nom ou au vôtre ?",
    answer:
      "Au nom de votre entreprise, sur son propre compte auprès de l'organisme. La qualification " +
      "vous appartient, les accès aussi. Nous intervenons sur votre compte, nous ne créons pas " +
      "un compte cabinet qui vous rendrait dépendant de nous.",
  },
  {
    question: "Que comprend le forfait, et qu'est-ce qui s'y ajoute ?",
    answer:
      "Le forfait couvre notre travail : diagnostic, collecte, montage, dépôt et réponses aux " +
      "demandes de compléments jusqu'à la décision. S'y ajoutent les frais d'instruction de " +
      "l'organisme, que vous lui versez directement, et — pour une certification de système — " +
      "l'audit facturé par l'organisme accrédité. Ces montants vous sont communiqués au diagnostic.",
  },
  {
    question: "Et si la commission refuse le dossier ?",
    answer:
      "Nous ne garantissons aucune obtention : la décision appartient à l'organisme, qui juge vos " +
      "moyens et vos références, pas notre travail de montage. Le forfait n'est pas remboursé. " +
      "En revanche le diagnostic sert précisément à ne pas déposer un dossier voué au refus : " +
      "s'il vous manque une référence ou une année d'ancienneté, nous vous le disons avant, " +
      "gratuitement, et nous vous disons quand revenir.",
  },
];

// --- Bloc 9 : titre de la section d'accueil ---------------------------------
// Déplacé depuis App.js le 18/09/2026 (revue finale) : c'était la seule copie
// commerciale de ce métier encore hors de ce fichier à lignes rouges.
export const TITRE_SECTION = {
  ligne1: "Qualibat, RGE, Qualifelec.",
  ligne2: "Le dossier que vous repoussez depuis deux ans.",
};

// --- Bloc 10 : la carte du pont (section d'accueil « Nos métiers ») ---------
export const CARTE_PONT = {
  titre: "Qualifications professionnelles",
  texte:
    "Qualibat, QualiPV, Qualifelec, RGE, ISO. La qualification que l'acheteur " +
    "réclame dans sa consultation, et que vos clients particuliers cherchent avant " +
    "de vous appeler. Nous montons le dossier, de la collecte des pièces à la décision.",
  lien: "Découvrir notre accompagnement",
};

// --- Bloc 11 : l'onglet d'offre « Les trois cas de figure » ----------------
// Les montants restent dans OFFRE_QUALIFICATIONS (src/config.js) : ils changent
// plus souvent que ce texte et ne doivent pas être dupliqués ici.
export const CAS_DE_FIGURE_TITRE = "Les trois cas de figure";

export const CAS_DE_FIGURE = [
  {
    cas: "Première demande",
    montantCle: "premiere",
    texte: "Qualibat, Qualit'EnR ou Qualifelec, mention RGE comprise quand elle s'applique.",
  },
  {
    cas: "Renouvellement",
    montantCle: "renouvellement",
    texte:
      "vos données administratives sont déjà constituées ; restent les nouvelles références " +
      "de chantier et leurs preuves.",
  },
  {
    cas: "Certification de système",
    montantCle: "systemes",
    texte:
      "ISO 9001, ISO 14001 ou MASE. Périmètre chiffré après diagnostic : il dépend de votre " +
      "effectif et de ce qui existe déjà chez vous.",
  },
];

// --- Bloc 12 : bandeau d'appel à l'action (onglet Offres) -------------------
export const CTA_BANNIERE = {
  titre: "Une qualification ne se rattrape pas le jour où l'acheteur la demande",
  texte:
    "Elle se prépare pendant que vous avez encore le temps de rassembler vos références. " +
    "Le diagnostic d'éligibilité est gratuit.",
  bouton: "Vérifier mon éligibilité",
};

// --- Bloc 13 : les guides publiés ------------------------------------------
// Ajouté le 18/09/2026. Ces six pages existaient déjà en ligne mais n'avaient
// AUCUN lien dans le site rendu : elles ne figuraient que dans le <noscript>
// de l'accueil, donc visibles de Google seul et d'aucun visiteur.
// Ordre volontaire : les deux premières sont les angles les moins disputés du
// benchmark du 18/09 (transparence tarifaire, requêtes d'urgence).
export const GUIDES = [
  {
    href: "/prix-qualification-rge-qualibat",
    titre: "Combien coûte une qualification RGE ou Qualibat ?",
    texte:
      "Les trois coûts qu'on confond — frais d'organisme, accompagnement, coûts induits — " +
      "et nos tarifs affichés.",
  },
  {
    href: "/dossier-qualification-refuse",
    titre: "Dossier refusé : que vous reste-t-il ?",
    texte:
      "Les motifs, les délais de recours, et pourquoi l'appel n'est pas suspensif.",
  },
  {
    href: "/audit-rge-non-conformite",
    titre: "Audit RGE et non-conformité",
    texte:
      "Les deux fenêtres de trois mois qui séparent une non-conformité d'un retrait.",
  },
  {
    href: "/renouvellement-qualification-qualibat",
    titre: "Renouveler sa qualification",
    texte:
      "Un cycle de quatre ans, un dossier à constituer huit mois avant l'échéance, " +
      "et l'attestation qu'il fallait demander bien plus tôt.",
  },
  {
    href: "/qualifications-qualit-enr",
    titre: "Qualit'EnR : QualiPV, QualiPAC, Qualibois, Qualisol",
    texte:
      "Une marque par énergie, un cycle d'audits — et deux qualifications qui ne portent " +
      "pas la mention RGE.",
  },
  {
    href: "/rge-qualibat-qualifelec-differences",
    titre: "RGE, Qualibat, Qualifelec : qui fait quoi ?",
    texte:
      "La mention RGE n'est pas un organisme. Qui qualifie quoi, et les trois erreurs " +
      "qui coûtent un cycle entier.",
  },
];
